interface RuntimeConfig {
  DEFAULT_ORG_SLUG?: string
  SUPERADMIN_DEFAULT_ORG_SLUG?: string
}

/**
 * Config precedence: `window.__CH_CONFIG__` (written by the container entrypoint
 * from env on each start) wins; otherwise `import.meta.env` (VITE_*, baked at
 * build time — used for `pnpm dev` / non-Docker builds).
 */
function runtimeValue(key: keyof RuntimeConfig): string | undefined {
  const rc = (globalThis as { __CH_CONFIG__?: RuntimeConfig }).__CH_CONFIG__
  const v = rc?.[key]?.trim()
  return v || undefined
}

/**
 * The org slug to fall back to when the URL has no slug (bare `/`,
 * `/dashboard`, post-login redirect, unknown route). Must match the bootstrap
 * org's slug in the DB (`DEFAULT_ORG_SLUG` on the API).
 */
export const DEFAULT_ORG_SLUG =
  runtimeValue('DEFAULT_ORG_SLUG') ??
  (import.meta.env.VITE_DEFAULT_ORG_SLUG as string | undefined)?.trim() ??
  'default'

/**
 * HQ org slug for the superadmin role. The backend stamps this slug's org id
 * into the superadmin JWT (see backend/api/routes/auth.ts); the frontend only
 * uses it as a safety fallback and for docs. Env-driven
 * (SUPERADMIN_DEFAULT_ORG_SLUG || DEFAULT_ORG_SLUG) — never hardcoded.
 */
export const SUPERADMIN_DEFAULT_ORG_SLUG =
  (runtimeValue('SUPERADMIN_DEFAULT_ORG_SLUG') ??
    (import.meta.env.VITE_SUPERADMIN_DEFAULT_ORG_SLUG as string | undefined)?.trim()) ||
  DEFAULT_ORG_SLUG

/** Bare auth entry points that render without a `/{slug}` prefix. */
const BARE_AUTH_FIRST_SEGMENTS = new Set([
  'invitation',
  'login',
  'select-org',
  'register',
  'forgot-password',
  'reset-password',
  'oauth',
  // NOTE: `oauth2` (Hydra login/consent for MCP clients) is deliberately NOT
  // exempt: bare `/oauth2/*` keeps the legacy redirect to
  // `/<defaultOrg>/oauth2/*` so the Claude/MCP OAuth flow always lands
  // slug-scoped and `login()` takes the unchanged org-scoped branch.
])

/**
 * True for bare auth URLs (`/login`, `/register`, `/oauth/...`) that must
 * render without forcing a `/{slug}` redirect (see main.tsx). Every other
 * bare path keeps the existing default-org redirect.
 */
export function isBareAuthRoute(pathname = window.location.pathname): boolean {
  const segments = pathname.split('/').filter(Boolean)
  const first = segments[0]
  if (!first) return false
  if (!BARE_AUTH_FIRST_SEGMENTS.has(first)) return false
  // A slug-prefixed auth URL (/<slug>/login) resolves a slug; only the bare
  // form (first segment itself is the auth word) is exempt.
  return resolveOrgSlugFromPath(pathname) === undefined
}

function resolveOrgSlugFromPath(path: string): string | undefined {
  const segments = path.split('/').filter(Boolean)
  const candidate = segments[0]
  if (!candidate || RESERVED.has(candidate)) return undefined
  return candidate
}

/**
 * Auth pages that live only at bare URLs. A slug-prefixed visit
 * (`/<slug>/login`, `/<slug>/register`, …) must redirect to the bare form
 * (see main.tsx) — slug-prefixed auth URLs are not allowed.
 */
const SLUG_AUTH_REDIRECT_SEGMENTS = new Set([
  'login',
  'select-org',
  'register',
  'forgot-password',
  'reset-password',
])

/**
 * Bare target for a slug-prefixed auth page URL, or null when no redirect
 * applies (bare URLs, non-auth pages, OAuth callbacks under a slug).
 */
export function slugAuthRedirectTarget(
  pathname = window.location.pathname,
  search = window.location.search,
  hash = window.location.hash
): string | null {
  const slug = resolveOrgSlugFromPath(pathname)
  if (slug === undefined) return null
  const inner = pathname.slice(slug.length + 1) || '/'
  const first = inner.split('/').filter(Boolean)[0]
  if (!first || !SLUG_AUTH_REDIRECT_SEGMENTS.has(first)) return null
  return `${inner}${search}${hash}`
}

// Must stay in sync with backend RESERVED_SYSTEM_SLUGS (tenant-resolver.ts)
// plus frontend app-route words. 'default' is intentionally NOT in the system set —
// the bootstrap org slug must resolve (see backend/tenancy/reserved-slugs.ts).
const RESERVED = new Set([
  'invitation',
  // Backend reserved (system routes) — mirrors RESERVED_SYSTEM_SLUGS
  'api',
  'admin',
  'health',
  'mcp',
  'status',
  'auth',
  'docs',
  'csrf-token',
  'metrics',
  // Frontend reserved (app routes)
  'login',
  'select-org',
  'register',
  'forgot-password',
  'reset-password',
  'dashboard',
  'projects',
  'search',
  'memory',
  'settings',
  'setup',
  'oauth',
  'oauth2',
])

/**
 * Resolve the current org slug from the URL path.
 * Returns undefined when no slug is present (bare/reserved first segment).
 *
 * URL shape: /:orgSlug/projects, /:orgSlug/login, etc.
 * The first path segment is the org slug (e.g. "acme", "wipro").
 */
export function resolveOrgSlug(): string | undefined {
  const path = window.location.pathname
  const segments = path.split('/').filter(Boolean)
  const candidate = segments[0]

  if (!candidate || RESERVED.has(candidate)) return undefined
  return candidate
}

/**
 * Get the token storage key for the current org.
 * When multi-tenancy is on, tokens are keyed per org so two tabs
 * on different orgs don't overwrite each other's tokens.
 */
export function tokenKey(base: string): string {
  const slug = resolveOrgSlug()
  return slug ? `${base}:${slug}` : base
}

/**
 * The org slug for the current context: the one in the URL, else the default.
 */
export function currentOrgSlug(): string {
  return resolveOrgSlug() ?? DEFAULT_ORG_SLUG
}

/**
 * In-app router path helpers. The React Router `basename` (set in `main.tsx`
 * to `/<orgSlug>`) already prefixes every absolute path, so these just
 * normalize and exist as a single call site to change if that ever moves.
 */
export function orgPath(path = ''): string {
  return path.startsWith('/') ? path : `/${path}`
}

export function homePath(): string {
  return '/dashboard'
}
